import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import { type CsmCandidatesStatus, csmLabel, type LicenceCsm } from '../domain/licence-csm';
import { LicenceFieldLabel } from './licence-field';
import { licenceFieldSx } from './licence-field.styles';

export interface CsmFieldProps {
  id: string;
  /** The picked user's id; `''` = none picked yet. */
  value: string;
  /** The people to offer (`csmOptions`: the candidates, plus the CSM on record). */
  options: readonly LicenceCsm[];
  status: CsmCandidatesStatus;
  onChange: (value: string) => void;
  /** The field's validation message, once it should show. */
  error?: string;
  disabled?: boolean;
}

const PLACEHOLDER = 'Choose a CSM';

const HELPER: Record<CsmCandidatesStatus, string | undefined> = {
  idle: undefined,
  loading: 'Loading the CSMs…',
  loaded: undefined,
  failed: "The CSMs couldn't be loaded.",
};

/**
 * "Customer Success Manager" (#583): a select over Invyo's active admins,
 * whatever their role (#630). Required since #695, with no "None": every
 * licence names a CSM, whom the client's contact form writes to. The same field
 * recipe as the category select. While the candidates load, the current value
 * still reads by name (it is among `options`), so the form never flashes an
 * empty field.
 */
export default function CsmField({
  id,
  value,
  options,
  status,
  onChange,
  error,
  disabled,
}: CsmFieldProps) {
  const byId = new Map(options.map((o) => [o.id, o]));
  return (
    <div>
      <LicenceFieldLabel id={`${id}-label`}>CSM</LicenceFieldLabel>
      <TextField
        id={id}
        select
        fullWidth
        required
        hiddenLabel
        variant="filled"
        value={byId.has(value) ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        error={Boolean(error)}
        helperText={error ?? HELPER[status]}
        disabled={disabled}
        slotProps={{
          input: { disableUnderline: true },
          select: {
            displayEmpty: true,
            // The visible label names the combobox; `hiddenLabel` leaves none.
            SelectDisplayProps: { 'aria-labelledby': `${id}-label` },
            renderValue: (selected) => {
              const picked = byId.get(selected as string);
              return picked ? csmLabel(picked) : PLACEHOLDER;
            },
          },
        }}
        sx={(t) => ({
          ...licenceFieldSx(t),
          '& .MuiSelect-select': { color: value ? 'text.primary' : 'text.disabled' },
        })}
      >
        {options.map((csm) => (
          <MenuItem key={csm.id} value={csm.id}>
            {csmLabel(csm)}
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
}
